import Router from './routes'
import ThemeConfig from './theme'
import { useAuth } from './hooks/useAuth'
import CircularProgress from '@mui/material/CircularProgress'
import { useContext, useEffect, useMemo, useRef, useState } from 'react'
import { Request, REQUEST_MESSAGE_TYPES } from 'schemas/request'
import { generateID } from 'utils/generateMessageID'
import NotistackProvider from './components/NotistackProvider'
import ErrorNotificationListener from './components/ErrorNotificationListener'
import { ConfigContext } from './contexts/ConfigurationContext'
import { setAuthenticated } from 'redux/slices/socket-auth-status'
import { useDispatch, useSelector, persistor } from './redux/store'
import { Box } from '@mui/material'
import { QueryContractCriteriaEnum } from '@types'
import useIdleTimeout from 'hooks/useIdleTimeout'
import { clearAllReduxCaches } from 'pages/dashboard/utils'
import { immediateReduxReset } from 'utils/immediateLogout'
import { clearPersistedFormData } from 'utils/localStorage'
import { useLocation, useNavigate } from 'react-router-dom'
import { useSocketConnection } from './hooks/useSocketConnection'
import { useAuthHook } from 'hooks/useAuthHook'
import { SocketHealthMonitor } from './components/SocketHealthMonitor'
import { DisconnectionWarning } from './components/DisconnectionWarning'
import { useNetworkStatus } from './hooks/useNetworkStatus'
import { RootState } from './redux/store'

export default function App() {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const { pathname } = useLocation()
  const { logout } = useAuth()
  const config = useContext(ConfigContext)
  const isAuthenticated = useAuthHook()
  const ranOnce = useRef(false)

  const { onSocketSend, waitForAuthentication, isAuthenticated: socketAuthenticated } = useSocketConnection()
  const networkStatus = useNetworkStatus()
  const socketHealth = useSelector((state: RootState) => state.socketHealth)

  // Manage disconnection warning dialog
  const [showDisconnectionWarning, setShowDisconnectionWarning] = useState(false)
  const [disconnectionReason, setDisconnectionReason] = useState<'network-offline' | 'socket-disconnected' | 'socket-timeout'>('socket-disconnected')
  const lastWarningTime = useRef(0)
  const previousSocketAuthState = useRef(false) // Track previous socket auth state
  const hasEverBeenAuthenticated = useRef(false) // Track if socket was ever authenticated

  const idleTimeout = useMemo(() => {
    const minutes = parseFloat(config?.REACT_APP_IDLE_TIMEOUT || '15') // default 15 minutes
    const ms = minutes * 60 * 1000
    return ms
  }, [config?.REACT_APP_IDLE_TIMEOUT])

  const warningDuration = useMemo(() => {
    const minutes = parseFloat(config?.REACT_APP_WARNING_DURATION || '1') // default 1 minute
    const ms = minutes * 60 * 1000
    return ms
  }, [config?.REACT_APP_WARNING_DURATION])

  const handleLogout = async () => {
    // Immediate cleanup
    immediateReduxReset()
    clearAllReduxCaches(dispatch)
    clearPersistedFormData()
    
    // Purge persisted Redux state
    await persistor.purge()
    
    // Perform logout
    logout()
    navigate('/auth/login')
  }

  const { WarningPopup } = useIdleTimeout({
    timeout: idleTimeout,
    warningTime: warningDuration,
    onIdle: () => {},
    onLogout: handleLogout
  })

  // Track socket authentication state changes
  useEffect(() => {
    // Update previous state
    if (socketAuthenticated) {
      hasEverBeenAuthenticated.current = true
    }

    // Detect disconnection (was connected, now isn't)
    const wasConnected = previousSocketAuthState.current
    const isNowDisconnected = !socketAuthenticated

    if (
      wasConnected && 
      isNowDisconnected && 
      hasEverBeenAuthenticated.current &&
      isAuthenticated // Only if user is logged in
    ) {
      // Only show warning if this is an actual disconnection, not initial connection failure
      const now = Date.now()
      if (now - lastWarningTime.current > 5000) {
        setDisconnectionReason('socket-disconnected')
        setShowDisconnectionWarning(true)
        lastWarningTime.current = now
      }
    }

    // Auto-close warning when connection is restored
    if (
      socketAuthenticated && 
      showDisconnectionWarning && 
      disconnectionReason === 'socket-disconnected'
    ) {
      setShowDisconnectionWarning(false)
    }

    // Update previous state after check
    previousSocketAuthState.current = socketAuthenticated
  }, [socketAuthenticated, isAuthenticated, showDisconnectionWarning, disconnectionReason])

  // Monitor network status separately
  useEffect(() => {
    const now = Date.now()
    
    // Show warning if network is offline
    if (!networkStatus.isOnline && now - lastWarningTime.current > 5000) {
      setDisconnectionReason('network-offline')
      setShowDisconnectionWarning(true)
      lastWarningTime.current = now
    }
    
    // Auto-close when network comes back online
    if (networkStatus.isOnline && showDisconnectionWarning && disconnectionReason === 'network-offline') {
      setShowDisconnectionWarning(false)
    }
  }, [networkStatus.isOnline, showDisconnectionWarning, disconnectionReason])

  // Monitor ping timeout separately
  useEffect(() => {
    const now = Date.now()
    
    if (socketHealth.isPingTimedOut && now - lastWarningTime.current > 5000) {
      setDisconnectionReason('socket-timeout')
      setShowDisconnectionWarning(true)
      lastWarningTime.current = now
    }
    
    // Auto-close when ping recovers (not timed out anymore)
    if (!socketHealth.isPingTimedOut && showDisconnectionWarning && disconnectionReason === 'socket-timeout') {
      setShowDisconnectionWarning(false)
    }
  }, [socketHealth.isPingTimedOut, showDisconnectionWarning, disconnectionReason])

  const triggerInitialSocketCalls = async () => {
    const requests: Request[] = [
      {
        messageId: generateID(),
        messageType: REQUEST_MESSAGE_TYPES.QueryContract,
        QueryContract: { criteria: QueryContractCriteriaEnum.lastActioned, limit: 500 }
      },
      {
        messageId: generateID(),
        messageType: REQUEST_MESSAGE_TYPES.QueryCreateActions,
        QueryCreateActions: {}
      },
      {
        messageId: generateID(),
        messageType: REQUEST_MESSAGE_TYPES.QueryNotifications,
        QueryNotifications: {
          isAcknowledged: false,
          limit: 1000
        }
      },
      {
        messageId: generateID(),
        messageType: REQUEST_MESSAGE_TYPES.QueryPurposeTypes,
        QueryPurposeTypes: {}
      }
    ]

    for (const req of requests) {
      const success = await onSocketSend(req)
      if (!success) {
        break
      }
    }
  }

  // 🔐 Register auth callback exactly once
  useEffect(() => {
    (async () => {
      await waitForAuthentication()
      if (ranOnce.current) return
      ranOnce.current = true
      dispatch(setAuthenticated(true))
      triggerInitialSocketCalls()
    })()
  }, [waitForAuthentication, dispatch])

  // 🔁 Scroll restoration
  useEffect(() => {
    window.scrollTo(0, 0)
  }, [pathname])

  return (
    <ThemeConfig>
      <NotistackProvider>
        <ErrorNotificationListener />
        {isAuthenticated ? (
          <>
            <WarningPopup />
            <SocketHealthMonitor />
            <DisconnectionWarning
              open={showDisconnectionWarning}
              onClose={() => setShowDisconnectionWarning(false)}
              reason={disconnectionReason}
            />
            <Router />
          </>
        ) : (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
            <CircularProgress />
          </Box>
        )}
      </NotistackProvider>
    </ThemeConfig>
  )
}
